import {
  EmptyState,
  EmptyStateContainer,
} from 'module/ui/components/atoms/empty-state';
import { Button, Loader } from '@rocketlane/ui';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';

// Set up PDF.js worker - using local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PDFViewerContainer = styled.div`
  height: 100%;
  overflow: auto;
  .react-pdf__Page__canvas {
    margin: 0 auto;
  }
`;

interface PDFViewerProps {
  url: string;
  fileName: string;
  onDownload?: () => void;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  fileName,
  onDownload,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (_error: Error) => {
    setError('failed_load_pdf');
    setLoading(false);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const link = document.createElement('a');

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (error) {
    return (
      <EmptyStateContainer>
        <EmptyState
          title={
            <FormattedMessage id="knowledge_base.file_viewer.failed_load_pdf" />
          }
          primaryCTA={
            <Button variant="primary" onClick={handleDownload}>
              <FormattedMessage id="knowledge_base.file_viewer.download_file" />
            </Button>
          }
        />
      </EmptyStateContainer>
    );
  }

  return (
    <PDFViewerContainer>
      {loading && (
        <div className="flex justify-center items-center h-full">
          <Loader size={32} />
        </div>
      )}
      <Document
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading=""
        error=""
      >
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            width={Math.min(window.innerWidth * 0.6, 800)}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        ))}
      </Document>
    </PDFViewerContainer>
  );
};
